'use client';

import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useI18n } from '@/shared/providers/I18nProvider';

export type ToolProps = ComponentProps<'div'>;

export function Tool({ className, ...props }: ToolProps) {
  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-card p-3 text-sm shadow-sm',
        className
      )}
      {...props}
    />
  );
}

export type ToolHeaderProps = {
  type?: string;
  state: string;
  className?: string;
};

const getStatusBadge = (status: string, dict: ReturnType<typeof useI18n>['dict']) => {
  const colors: Record<string, string> = {
    'input-streaming': 'bg-secondary text-secondary-foreground',
    'input-available': 'bg-primary/10 text-primary',
    'approval-requested': 'bg-secondary text-muted-foreground',
    'approval-responded': 'bg-primary/10 text-primary',
    'output-available': 'bg-primary/10 text-primary',
    'output-error': 'bg-destructive/10 text-destructive',
    'output-denied': 'bg-destructive/10 text-destructive',
  };

  const labels: Record<string, string> = {
    'input-streaming': dict.common.toolUi.status.pending,
    'input-available': dict.common.toolUi.status.running,
    'approval-requested': dict.common.toolUi.status.pending,
    'approval-responded': dict.common.toolUi.status.approved,
    'output-available': dict.common.toolUi.status.completed,
    'output-error': dict.common.toolUi.status.error,
    'output-denied': dict.common.toolUi.status.denied,
  };

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-1 text-xs font-medium',
        colors[status] || 'bg-secondary text-muted-foreground'
      )}
    >
      {labels[status] || status}
    </span>
  );
};

export function ToolHeader({ className, type, state }: ToolHeaderProps) {
  const { dict } = useI18n();
  return (
    <div className={cn('flex items-center justify-between', className)}>
      <div className="flex items-center gap-2">
        <span className="font-semibold text-foreground">
          {type || dict.common.toolUi.toolLabel}
        </span>
      </div>
      {getStatusBadge(state, dict)}
    </div>
  );
}

export type ToolContentProps = ComponentProps<'div'>;

export function ToolContent({ className, ...props }: ToolContentProps) {
  return <div className={cn('mt-2', className)} {...props} />;
}

export type ToolInputProps = ComponentProps<'div'> & {
  input: unknown;
};

export function ToolInput({ className, input, ...props }: ToolInputProps) {
  const { dict } = useI18n();
  return (
    <div className={cn('mt-2', className)} {...props}>
      <div className="text-xs font-medium text-muted-foreground">
        {dict.common.toolUi.parametersLabel}
      </div>
      <pre className="mt-1 overflow-x-auto rounded bg-muted/50 p-2 text-xs text-foreground">
        {JSON.stringify(input, null, 2)}
      </pre>
    </div>
  );
}

export type ToolOutputProps = ComponentProps<'div'> & {
  output?: ReactNode;
  errorText?: string;
};

export function ToolOutput({
  className,
  output,
  errorText,
  ...props
}: ToolOutputProps) {
  const { dict } = useI18n();
  if (!(output || errorText)) {
    return null;
  }

  return (
    <div className={cn('mt-2', className)} {...props}>
      <div className="text-xs font-medium text-muted-foreground">
        {errorText ? dict.common.toolUi.errorLabel : dict.common.toolUi.resultLabel}
      </div>
      {errorText && (
        <div className="mt-1 rounded bg-destructive/10 p-2 text-xs text-destructive">
          {errorText}
        </div>
      )}
      {output && <div className="mt-1">{output}</div>}
    </div>
  );
}
